import { CircleCheck, CircleX, Copy, Pencil, Trash2 } from "lucide-mui";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import FormattedDate from "../../../components/FormattedDate.tsx";
import type { InferenceFleet, InferenceInstance } from "../../../api/client";
import { api } from "../../../api/client";
import { copyToClipboard } from "../../../utils/clipboard.ts";
import { keys } from "../../../api/queryKeys";
import DropdownMenu from "../../../components/DropdownMenu";
import HeartbeatChip from "../../../components/HeartbeatChip";
import RowActions from "../../../components/RowActions.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import Sparkline from "../../../components/Sparkline.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatNumber } from "../../../utils/format.ts";
import PageHeader from "../../../components/PageHeader.tsx";
import InferenceRentalsPanel from "./InferenceRentalsPanel.tsx";

const STATUS_TABS = ["approved", "pending", "rejected"] as const;

function formatGpu(instance: InferenceInstance): string {
  const parts: string[] = [];
  if (instance.gpu_name) parts.push(instance.gpu_name);
  if (instance.vram_gb != null) parts.push(`${instance.vram_gb}GB`);
  if (instance.gpu_count != null && instance.gpu_count > 1) parts.push(`${instance.gpu_count}×`);
  const fleet = instance.extra_info?.fleet;
  if (typeof fleet === "string") parts.push(fleet);
  return parts.join(" · ") || "—";
}

function RejectDialog({
  onClose,
  onConfirm,
  loading,
}: {
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reject instance</DialogTitle>
      <DialogContent>
        <TextField
          label="Reason (optional)"
          fullWidth
          multiline
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => onConfirm(reason)}
          loading={loading}
        >
          Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RoutingEditDialog({
  instance,
  onClose,
  onSave,
  loading,
}: {
  instance: InferenceInstance;
  onClose: () => void;
  onSave: (body: {
    weight: number;
    bulk_only: boolean;
    interactive_only: boolean;
    api_key?: string;
  }) => void;
  loading: boolean;
}) {
  const [value, setValue] = useState(String(instance.weight));
  const [bulkOnly, setBulkOnly] = useState(instance.bulk_only);
  const [interactiveOnly, setInteractiveOnly] = useState(instance.interactive_only);
  const [apiKey, setApiKey] = useState("");
  const selfLimited = instance.max_requests_per_minute != null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit routing: {instance.hostname}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {selfLimited
            ? `This instance says it takes ${formatNumber(instance.max_requests_per_minute ?? 0)} requests per minute, and every dispatcher together stays under that. Weight doesn't apply.`
            : "Higher weight = more traffic. Use relative GPU performance (e.g. B200=2.0, DGX=1.0)."}
        </Typography>
        <TextField
          label="Weight"
          type="number"
          disabled={selfLimited}
          slotProps={{
            htmlInput: { min: 0.01, step: 0.1 },
            input: { startAdornment: <InputAdornment position="start">×</InputAdornment> },
          }}
          fullWidth
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Switch
              checked={bulkOnly}
              onChange={(e) => {
                setBulkOnly(e.target.checked);
                if (e.target.checked) setInteractiveOnly(false);
              }}
            />
          }
          label="Bulk callers only"
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Only signal evaluation and company classification use it: single-shot calls that can
          wait in a queue.
        </Typography>
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Switch
              checked={interactiveOnly}
              onChange={(e) => {
                setInteractiveOnly(e.target.checked);
                if (e.target.checked) setBulkOnly(false);
              }}
            />
          }
          label="Interactive callers only"
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Signal evaluation and company classification never use it; agents, confirms of bulk
          matches and other one-off calls still do. For a rig whose GPU a decision model shares.
        </Typography>
        <TextField
          label="API key"
          type="password"
          autoComplete="off"
          fullWidth
          sx={{ mt: 3 }}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          helperText={
            instance.api_key
              ? "Set. Leave empty to keep it. With a key, heartbeats can't move the API URL."
              : "Sent as a bearer token on every request, e.g. the Hetzner proxy's PROXY_API_KEY."
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() =>
            onSave({
              weight: parseFloat(value),
              bulk_only: bulkOnly,
              interactive_only: interactiveOnly,
              ...(apiKey ? { api_key: apiKey } : {}),
            })
          }
          loading={loading}
          disabled={!(parseFloat(value) > 0)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CopyInstallCommandButton() {
  const fleetsQ = useQuery({
    queryKey: keys.inferenceInstance.fleets(),
    queryFn: api.listInferenceFleets,
  });
  const copy = useMutation({
    mutationFn: async (fleet: InferenceFleet) => {
      const { url } = await api.getInferenceInstallLink(fleet.slug);
      await copyToClipboard(
        `curl -fsSL '${url}' | bash`,
        `Install command for ${fleet.label} copied (valid ~2 min)`,
      );
    },
  });
  const handleCopy = (fleet: InferenceFleet) => {
    if (!copy.isPending) copy.mutate(fleet);
  };
  return (
    <DropdownMenu
      label="Copy install command"
      tooltip="Copy a one-line install command for a new GPU node. Pick the node's fleet so the vLLM config matches its hardware. Link is valid for ~2 minutes."
      align="right"
      buttonProps={{
        variant: "outlined",
        startIcon: <Copy fontSize="small" />,
        loading: copy.isPending,
        loadingPosition: "start",
        disabled: !fleetsQ.data?.length,
      }}
    >
      {fleetsQ.data?.map((fleet) => (
        <MenuItem key={fleet.slug} onClick={() => handleCopy(fleet)}>
          <ListItemText primary={fleet.label} secondary={fleet.description} />
          {!fleet.validated && (
            <Chip label="Untested" size="small" color="warning" variant="outlined" sx={{ ml: 2 }} />
          )}
        </MenuItem>
      ))}
    </DropdownMenu>
  );
}

const TOKEN_BUCKET_SECONDS = 15 * 60;
const TOKEN_BUCKETS = 96;

/** 24h of 15-minute buckets, oldest first, with quiet buckets as zero. */
function tokenSeries(buckets: NonNullable<InferenceInstance["token_buckets"]>) {
  const byStart = new Map(buckets.map((b) => [b.t, b.p + b.g]));
  const current =
    Math.floor(Date.now() / 1000 / TOKEN_BUCKET_SECONDS) * TOKEN_BUCKET_SECONDS;
  return Array.from({ length: TOKEN_BUCKETS }, (_, i) => {
    const t = current - (TOKEN_BUCKETS - 1 - i) * TOKEN_BUCKET_SECONDS;
    return { t, v: byStart.get(t) ?? 0 };
  });
}

function TokenThroughput({ inst }: { inst: InferenceInstance }) {
  if (inst.tokens_per_sec_24h == null) {
    return <Typography variant="body2" sx={{ color: "text.secondary" }}>—</Typography>;
  }
  const tooltip = [
    `Lifetime: ${formatNumber(inst.tokens_prompt_total)} prompt, ` +
      `${formatNumber(inst.tokens_generation_total)} generation tokens`,
    "Average over the last 24h (idle time included)",
  ].join(". ");
  return (
    <Tooltip title={tooltip}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Typography variant="body2">
          {formatNumber(Math.round(inst.tokens_per_sec_24h * 10) / 10)} tok/s
        </Typography>
        <Sparkline
          variant="area"
          showRange={false}
          width={120}
          height={28}
          values={tokenSeries(inst.token_buckets ?? []).map((b) => b.v)}
        />
      </Box>
    </Tooltip>
  );
}

export default function InferenceInstancesPage() {
  const [tab, setTab] = useState(0);
  const [rejectTarget, setRejectTarget] = useState<InferenceInstance | null>(null);
  const [routingTarget, setRoutingTarget] = useState<InferenceInstance | null>(null);

  const status = STATUS_TABS[tab];
  const queryClient = useQueryClient();

  const { data: allInstances = [], isLoading } = useQuery({
    queryKey: keys.inferenceInstance.list(),
    queryFn: () => api.listInferenceInstances(),
    // Pushed on every heartbeat (throttled), not just on admin changes.
    meta: { live: true },
  });

  const pendingCount = allInstances.filter((i) => i.status === "pending").length;
  const instances = allInstances.filter((i) => i.status === status);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: keys.inferenceInstance.all });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveInferenceInstance(id),
    onSuccess: () => { invalidate(); toast.success("Instance approved"); },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.rejectInferenceInstance(id, reason || undefined),
    onSuccess: () => { invalidate(); setRejectTarget(null); toast.success("Instance rejected"); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteInferenceInstance(id),
    onSuccess: () => { invalidate(); toast.success("Instance deleted"); },
  });

  const routingMutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      weight: number;
      bulk_only: boolean;
      interactive_only: boolean;
      api_key?: string;
    }) =>
      api.updateInferenceInstance(id, body),
    onSuccess: () => { invalidate(); setRoutingTarget(null); toast.success("Routing updated"); },
  });

  return (
    <Box>
      <PageHeader
        title="Inference instances"
        description={
          <>
            vLLM and decision-model servers that have registered with the platform. Approve
            instances to include them in the load balancer pool; rented boxes join it on their own.
          </>
        }
        actions={<CopyInstallCommandButton />}
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {STATUS_TABS.map((s) => (
          <Tab
            key={s}
            label={
              s === "pending" ? (
                <Badge
                  badgeContent={pendingCount}
                  color="warning"
                  sx={{ pr: pendingCount > 0 ? 1.5 : 0 }}
                >
                  Pending
                </Badge>
              ) : (
                s.charAt(0).toUpperCase() + s.slice(1)
              )
            }
          />
        ))}
      </Tabs>

      <TableCard loading={isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Host</TableCell>
              <TableCell>GPU</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>API URL</TableCell>
              {status === "approved" && <TableCell>Share</TableCell>}
              {status === "approved" && <TableCell>Load</TableCell>}
              {status === "approved" && <TableCell>24h reqs</TableCell>}
              {status === "approved" && <TableCell>24h tok/s</TableCell>}
              <TableCell>Last heartbeat</TableCell>
              <TableCell>Registered</TableCell>
              {status === "rejected" && <TableCell>Reason</TableCell>}
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {instances.map((inst) => (
              <TableRow key={inst.id} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{inst.hostname}</Typography>
                    {inst.kind === "decision" && <StatusChip kind="inference_routing" value="decision" />}
                    {inst.bulk_only && <StatusChip kind="inference_routing" value="bulk_only" />}
                    {inst.interactive_only && (
                      <StatusChip kind="inference_routing" value="interactive_only" />
                    )}
                  </Box>
                  {inst.public_ip && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {inst.public_ip}
                    </Typography>
                  )}
                  {inst.vllm_version && (
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                      {inst.kind === "decision" ? "SGLang" : "vLLM"} {inst.vllm_version}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatGpu(inst)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {inst.model_name ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}
                  >
                    {inst.api_url}
                  </Typography>
                </TableCell>
                {status === "approved" && (
                  <TableCell>
                    {inst.max_requests_per_minute != null ? (
                      <Tooltip title="The instance reports how many requests per minute it takes; all dispatchers together stay under it">
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                            {formatNumber(inst.max_requests_per_minute)} req/min
                          </Typography>
                          <StatusChip kind="inference_routing" value="self_limited" variant="outlined" />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2">×{inst.weight.toFixed(1)}</Typography>
                    )}
                  </TableCell>
                )}
                {status === "approved" && (
                  <TableCell>
                    {inst.requests_running != null ? (
                      <Tooltip title={`KV cache: ${inst.kv_cache_usage_pct?.toFixed(1) ?? "?"}%`}>
                        <Typography variant="body2">
                          {inst.requests_running} run / {inst.requests_waiting ?? 0} wait
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>—</Typography>
                    )}
                  </TableCell>
                )}
                {status === "approved" && (
                  <TableCell>
                    <Tooltip title="Requests routed to this instance in the last 24 hours">
                      <Typography variant="body2">
                        {formatNumber(inst.requests_last_24h)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                )}
                {status === "approved" && (
                  <TableCell>
                    <TokenThroughput inst={inst} />
                  </TableCell>
                )}
                <TableCell>
                  <HeartbeatChip iso={inst.last_heartbeat_at} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <FormattedDate value={inst.created_at} />
                  </Typography>
                </TableCell>
                {status === "rejected" && (
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {inst.rejection_reason ?? "—"}
                    </Typography>
                  </TableCell>
                )}
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={inst.hostname}
                    primary={
                      status === "pending"
                        ? {
                            label: "Approve",
                            icon: <CircleCheck fontSize="small" color="success" />,
                            disabled: approveMutation.isPending,
                            onClick: () => approveMutation.mutate(inst.id),
                          }
                        : status === "approved"
                          ? {
                              label: "Edit routing",
                              icon: <Pencil fontSize="small" />,
                              onClick: () => setRoutingTarget(inst),
                            }
                          : undefined
                    }
                    actions={[
                      {
                        label: "Approve",
                        icon: <CircleCheck fontSize="small" />,
                        hidden: status !== "rejected",
                        disabled: approveMutation.isPending,
                        onClick: () => approveMutation.mutate(inst.id),
                      },
                      {
                        label: "Reject",
                        icon: <CircleX fontSize="small" />,
                        hidden: status === "rejected",
                        disabled: rejectMutation.isPending,
                        onClick: () => setRejectTarget(inst),
                      },
                      {
                        label: "Delete",
                        icon: <Trash2 fontSize="small" />,
                        destructive: true,
                        onClick: () => deleteMutation.mutateAsync(inst.id),
                        confirm: { title: `Delete instance: ${inst.hostname}` },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {instances.length === 0 && (
              <EmptyTableRow colSpan={7 + (status === "approved" ? 4 : 0) + (status === "rejected" ? 1 : 0)}>
                No {status} instances
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>

      <InferenceRentalsPanel />

      {rejectTarget && (
        <RejectDialog
          key={rejectTarget.id}
          onClose={() => setRejectTarget(null)}
          onConfirm={(reason) => rejectMutation.mutate({ id: rejectTarget.id, reason })}
          loading={rejectMutation.isPending}
        />
      )}

      {routingTarget && (
        <RoutingEditDialog
          key={routingTarget.id}
          instance={routingTarget}
          onClose={() => setRoutingTarget(null)}
          onSave={(body) => routingMutation.mutate({ id: routingTarget.id, ...body })}
          loading={routingMutation.isPending}
        />
      )}
    </Box>
  );
}
